function toText(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return "";
}

function firstNonBlank(...values) {
  for (const value of values) {
    const text = toText(value);
    if (text) return text;
  }
  return "";
}

function normalizeChoice(option) {
  if (option === null || option === undefined) return null;

  if (typeof option === "string" || typeof option === "number" || typeof option === "boolean") {
    const text = toText(option);
    return text ? { value: text, label: text, raw: option } : null;
  }

  if (typeof option !== "object") return null;

  const value = firstNonBlank(
    option.value,
    option.uuid,
    option.id,
    option.key,
    option.code,
    option.name,
    option.title,
    option.label,
    option.state,
    option.lga,
    option.fullName,
    option.full_name,
    option.institution
  );

  if (!value) return null;

  const label = firstNonBlank(
    option.label,
    option.name,
    option.title,
    option.fullName,
    option.full_name,
    option.state,
    option.lga,
    option.institution,
    option.value,
    value
  ) || value;

  return { value, label, raw: option };
}

export function normalizeChoiceList(options = []) {
  const seen = new Set();
  const normalized = [];

  for (const option of Array.isArray(options) ? options.flat(Infinity) : [options]) {
    const item = normalizeChoice(option);
    if (!item || seen.has(item.value)) continue;
    seen.add(item.value);
    normalized.push(item);
  }

  return normalized;
}

export function optionsFromValues(values = []) {
  return normalizeChoiceList(values);
}

function resolveSelector(row, selector) {
  if (typeof selector === "function") return selector(row);
  if (typeof selector === "string") return row?.[selector];
  return undefined;
}

function collectTextValue(candidate) {
  if (candidate === null || candidate === undefined) return [];
  if (Array.isArray(candidate)) return candidate.flatMap((entry) => collectTextValue(entry));
  if (typeof candidate === "object") {
    const normalized = normalizeChoice(candidate);
    return normalized ? [normalized.label] : [];
  }

  const text = String(candidate).trim();
  return text ? [text] : [];
}

export function collectTextValues(rows = [], selectors = []) {
  const values = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    for (const selector of selectors) {
      const candidate = resolveSelector(row, selector);
      values.push(...collectTextValue(candidate));
    }
  }

  return [...new Set(values.filter(Boolean))];
}

export function optionsFromRows(rows = [], labelResolver, valueResolver) {
  const seen = new Set();
  const options = [];

  for (const row of Array.isArray(rows) ? rows : []) {
    const value = firstNonBlank(
      typeof valueResolver === "function" ? valueResolver(row) : undefined,
      row?.value,
      row?.uuid,
      row?.id
    );
    if (!value || seen.has(value)) continue;

    const label = firstNonBlank(
      typeof labelResolver === "function" ? labelResolver(row) : undefined,
      row?.label,
      row?.name,
      row?.title,
      value
    ) || value;

    seen.add(value);
    options.push({ value, label, raw: row });
  }

  return options;
}

export function mergeChoiceLists(...lists) {
  return normalizeChoiceList(lists.flat());
}

export function injectChoiceOption(options = [], option) {
  const normalized = normalizeChoice(option);
  const base = normalizeChoiceList(options);

  if (!normalized) return base;
  if (base.some((item) => item.value === normalized.value)) return base;

  return [normalized, ...base];
}
